import React, { useCallback } from "react";
import { Avatar, Button, Card, List, Popconfirm, Skeleton } from "antd";
import NotificationService from "../../services/NotificationService";
import Moment from "react-moment";
import LoadingPlaceholder from "../../components/utils/LoadingPlaceholder";
import { useNavigate } from "react-router-dom";
import { FolderOpenOutlined, KeyOutlined, ReloadOutlined } from "@ant-design/icons";
import Main from "../../components/layouts/Main";
import { useUserContext } from "../../contexts/UserContext";
import { useNotificationContext } from "../../contexts/NotificationContext";
import styles from "./NotificationListPage.module.scss";
import { catchError } from "../../services/DaoService";
import Breadcrumb from "../../components/utils/Breadcrumb";
import PageTitle from "../../components/utils/PageTitle";

const clockicon = (
    <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18ZM11 6C11 5.44772 10.5523 5 10 5C9.44772 5 9 5.44772 9 6V10C9 10.2652 9.10536 10.5196 9.29289 10.7071L12.1213 13.5355C12.5118 13.9261 13.145 13.9261 13.5355 13.5355C13.9261 13.145 13.9261 12.5118 13.5355 12.1213L11 9.58579V6Z"
            fill="#111827"
        />
    </svg>
);

export default function NotificationListPage() {
    const [notifications, setNotifications] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [paginationData, setPaginationData] = React.useState(null);
    const [loadingMore, setLoadingMore] = React.useState(false);
    const [isMarkAllAsRead, setIsMarkAllAsRead] = React.useState(false);
    const { loadUnreadNotifications, loadUnreadNotificationsCount } =
        useNotificationContext();
    const navigate = useNavigate();
    const { currentUser, check } = useUserContext();
    const [hasMore, setHasMore] = React.useState(false);

    const init = useCallback(() => {
        if (!currentUser) return;

        setNotifications([]);
        setLoading(true);
        NotificationService.getInstance()
            .findAllNotifications(currentUser.uid)
            .then((response) => setPaginationData(response.data))
            .catch(catchError)
            .finally(() => setLoading(false));
    }, [currentUser]);

    React.useEffect(() => {
        init();
    }, [init]);

    React.useEffect(() => {
        if (paginationData) {
            setHasMore(paginationData.next_page_url !== null);
            setNotifications((prevNotifications) => [
                ...prevNotifications,
                ...paginationData.data.filter(
                    (n) => !prevNotifications.find((notification) => notification.id === n.id)
                ),
            ]);
        }
    }, [paginationData]);

    const loadMore = () => {
        if (paginationData.next_page_url) {
            setLoadingMore(true);
            NotificationService.get(paginationData.next_page_url)
                .then((response) => setPaginationData(response.data))
                .catch(catchError)
                .finally(() => setLoadingMore(false));
        }
    };

    const handleRoute = (notification) => {
        console.log(notification);

        if (notification.read_at) {
            navigate(notification.data.url);
            return;
        }
        NotificationService.getInstance()
            .markAsRead(currentUser.uid, notification.id)
            .then(() => {
                loadUnreadNotifications();
                loadUnreadNotificationsCount();
                navigate(notification.data.url);
            })
            .catch(catchError);
    };

    const handleMarkAllAsRead = () => {
        setIsMarkAllAsRead(true);
        NotificationService.getInstance()
            .markAllAsRead(currentUser.uid)
            .then(() => {
                init();
                loadUnreadNotifications();
                loadUnreadNotificationsCount();
            })
            .catch(catchError)
            .finally(() => setIsMarkAllAsRead(false));
    };

    return (
        <Main>
            <Breadcrumb canSee={check('VIEW-DASHBOARD')} parentLink="/" parentLinkTitle="Accueil" title='Notifications' />
            <PageTitle title="Mes notifications" icon={<KeyOutlined />}>
                {/* refresh button  */}
                <Button loading={loading} type="default" icon={<ReloadOutlined />} onClick={init}>Rafraîchir</Button>
                <Popconfirm
                    title="Voulez-vous vraiment marquer toutes les notifications comme lues ?"
                    onConfirm={handleMarkAllAsRead}
                    okText="Oui"
                    cancelText="Non"
                >
                    <Button
                        loading={isMarkAllAsRead}
                        type="primary"
                        className={styles.markAllButton}
                        icon={<FolderOpenOutlined />}
                    >
                        Tout marquer comme lu
                    </Button>
                </Popconfirm>
            </PageTitle>
            {loading ? (
                <LoadingPlaceholder />
            ) : (
                <Card className={styles.notificationCard}>
                    <List
                        className={styles.notificationListContainer}
                        loading={loadingMore}
                        itemLayout="horizontal"
                        dataSource={notifications}
                        loadMore={
                            hasMore && (
                                <div className={styles.loadMoreButtonContainer}>
                                    <Button className={styles.loadMoreButton} onClick={loadMore} loading={loadingMore}>
                                        Charger plus
                                    </Button>
                                </div>
                            )
                        }
                        renderItem={(item) => (
                            <List.Item
                                className={`${styles.notificationItem} ${!item.read_at ? styles.unread : ""
                                    }`}
                                onClick={() => handleRoute(item)}
                            >
                                <Skeleton avatar title={false} loading={item.loading}>
                                    <List.Item.Meta
                                        avatar={<Avatar src={item.data.avatar} size="large" />}
                                        title={<div className={styles.notificationMeta}>{item.data.title}</div>}
                                        description={
                                            <div className={styles.notificationDescription}>
                                                {item.data.body}
                                                <div className={styles.notificationTimestamp}>
                                                    {clockicon} <Moment fromNow>{item.created_at}</Moment>
                                                </div>
                                            </div>
                                        }
                                    />
                                </Skeleton>
                            </List.Item>
                        )}
                    />
                </Card>
            )}
        </Main>
    );
}
